import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { useAbfFetches, useWhirlpoolsOffchainQuery, useUserTokens } from "@bridgesplit/abf-react";
import { AddLiquidityQuote, OpenPositionQuote, RemoveLiquidityQuote } from "@orca-so/whirlpool-sdk";
import { Slippage, SlippageValue } from "app/components/common";
import { TokenPositionExpanded, WhirlpoolPositionExpanded } from "@bridgesplit/abf-sdk";
import { lamportsToUiAmount } from "@bridgesplit/utils";
import BN from "bn.js";
import { useLocalStorage } from "@bridgesplit/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";

import {
    DepositForm,
    OpenPositionForm,
    WhirlpoolContextType,
    WhirlpoolContextValue,
    WhirlpoolPoolData,
    WhirlpoolPositionData,
    WithdrawForm
} from "./types";
import { getPoolData, getPositionData } from "./orca";
export const WhirlpoolContext = createContext<WhirlpoolContextType | null>(null);

export function useWhirlpoolContext() {
    const context = useContext(WhirlpoolContext);
    if (!context) {
        throw new Error("useWhirlpoolContext must be used within a WhirlpoolContext");
    }
    return context;
}

const WHIRLPOOL_SLIPPAGE_KEY = "whirlpool_slippage";
export const WHIRLPOOL_DEFAULT_SLIPPAGE_UI = 2.5;
export const AUTO_SLIPPAGE_DECIMALS = 0.1; // high slippage for auto-slippage

export function WhirlpoolProvider({ children, value }: { children: React.ReactNode; value: WhirlpoolContextValue }) {
    const [initialWithdrawQuote, setInitialWithdrawQuote] = useState<RemoveLiquidityQuote | undefined>(undefined);
    const [positionData, setPositionData] = useState<WhirlpoolPositionData | null | undefined>(undefined);
    const [poolData, setPoolData] = useState<WhirlpoolPoolData | null | undefined>(undefined);
    const [openPositionForm, setOpenPositionForm] = useState<OpenPositionForm>({});
    const [openPositionQuote, setOpenPositionQuote] = useState<OpenPositionQuote | undefined>(undefined);
    const [depositForm, setDepositForm] = useState<DepositForm>({});
    const [addLiquidityQuote, setAddLiquidityQuote] = useState<AddLiquidityQuote | undefined>(undefined);
    const [withdrawForm, setWithdrawForm] = useState<WithdrawForm>({});
    const [withdrawQuote, setWithdrawQuote] = useState<RemoveLiquidityQuote | undefined>(undefined);
    const [isFlippedPrice, setIsFlippedPrice] = useState(false);

    const { resetOrcaExternalApi, resetClmmPositions } = useAbfFetches();
    const { refresh } = useUserTokens();

    const [cachedSlippage, setCachedSlippage] = useLocalStorage<SlippageValue>(
        WHIRLPOOL_SLIPPAGE_KEY,
        WHIRLPOOL_DEFAULT_SLIPPAGE_UI
    );

    // get the fresh whirlpool position by using up to date withdraw quote
    const whirlpoolPosition = useMemo((): WhirlpoolPositionExpanded => {
        const originalPosition = value.whirlpoolPosition;
        if (!initialWithdrawQuote) return originalPosition;

        const updateTokenBalance = (token: TokenPositionExpanded, amount: BN): TokenPositionExpanded => {
            const uiAmount = lamportsToUiAmount(amount.toNumber(), token.decimals);
            return { ...token, amount: uiAmount, usdAmount: token.usdPrice * uiAmount };
        };

        const tokenA = updateTokenBalance(originalPosition.tokenA, initialWithdrawQuote.estTokenA);
        const tokenB = updateTokenBalance(originalPosition.tokenB, initialWithdrawQuote.estTokenB);

        return {
            ...originalPosition,
            totalPrice: tokenA.usdAmount + tokenB.usdAmount,
            tokenA,
            tokenB
        };
    }, [value.whirlpoolPosition, initialWithdrawQuote]);
    const { data: whirlpoolsOffchain } = useWhirlpoolsOffchainQuery(
        value.whirlpoolPosition.whirlpoolAddress ? { addresses: [value.whirlpoolPosition.whirlpoolAddress] } : skipToken
    );

    const whirlpoolOffchain = useMemo(() => {
        const whirlpoolAddress = value.whirlpoolPosition.whirlpoolAddress;
        return whirlpoolsOffchain?.data.find((whirlpool) => whirlpool.address === whirlpoolAddress);
    }, [value.whirlpoolPosition.whirlpoolAddress, whirlpoolsOffchain?.data]);

    const resetForms = useCallback(() => {
        setOpenPositionForm((prev) => ({
            ...prev,
            side: prev.side ?? "A",
            status: "silent-refetch"
        }));
        setWithdrawForm((prev) => ({
            ...prev,
            status: "silent-refetch"
        }));
        setDepositForm((prev) => ({
            ...prev,
            side: prev.side ?? "A",
            status: "silent-refetch"
        }));
    }, []);

    const slippageController = Slippage.useController({
        presetsUi: [1, 2.5, 5],
        defaultSlippageUi: cachedSlippage ?? WHIRLPOOL_DEFAULT_SLIPPAGE_UI,
        onEdit: (value) => {
            if (value !== undefined) {
                setCachedSlippage(value);
            }
            resetForms();
        },
        showAuto: true
    });

    const forceFetchOrcaData = useCallback(async () => {
        const [poolData, positionData] = await Promise.all([
            getPoolData(whirlpoolPosition.whirlpoolAddress),
            getPositionData(whirlpoolPosition)
        ]);
        setPoolData(poolData);
        setPositionData(positionData?.positionData ?? null);
        setInitialWithdrawQuote(positionData?.quote);
    }, [whirlpoolPosition]);

    const reset = useCallback(async () => {
        await forceFetchOrcaData();
        resetForms();
        resetOrcaExternalApi();
        void refresh();
        resetClmmPositions();
    }, [forceFetchOrcaData, resetForms, resetOrcaExternalApi, resetClmmPositions, refresh]);

    const poll = useCallback(async () => {
        resetForms();
        void refresh();
        await forceFetchOrcaData();
    }, [resetForms, forceFetchOrcaData, refresh]);

    const contextValue: WhirlpoolContextType = {
        ...value,
        initialWithdrawQuote,
        setInitialWithdrawQuote,
        whirlpoolPosition,
        whirlpoolOffchain,
        positionData,
        setPositionData,
        depositForm,
        setDepositForm,
        poolData,
        setPoolData,
        openPositionForm,
        setOpenPositionForm,
        openPositionQuote,
        setOpenPositionQuote,
        isFlippedPrice,
        setIsFlippedPrice,
        reset,
        poll,
        slippageController,
        addLiquidityQuote,
        setAddLiquidityQuote,
        withdrawForm,
        setWithdrawForm,
        withdrawQuote,
        setWithdrawQuote
    };

    return <WhirlpoolContext.Provider value={contextValue}>{children}</WhirlpoolContext.Provider>;
}
